import { Icon } from "@circle-react-shared/Icon";
import { Tab } from "@circle-react-uikit/TabV2";
import { CounterBadge } from "@circle-react/components/shared/CounterBadge";
import { roundedCount } from "@circle-react/helpers/notificationHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { Tab as CompassTab } from "@circleco/compass/components/Tabs";
import type { JSX } from "react";

import { BotChatList } from "@/react/components/Modals/MessagingModalCompass/LeftSidebar/List/BotChatList";
import {
  TAB_AGENTS,
  TAB_INBOX,
  TAB_UNREAD,
} from "@/react/components/shared/DirectMessages/constants";
import { useSmOrMdOrLgScreenMediaQuery } from "@/react/hooks/useMediaQuery";

import { DMsList } from "../List";
import { ListWrapper } from "../List/ListWrapper";
import { NoUnreadMessages } from "../NoUnreadMessages";

interface TabHeaderLegacyProps {
  dmSelectedTabIndex: number;
  setDmSelectedTabIndex: (index: number) => void;
  shouldShowAgentsTab: boolean;
  shouldShowMessagesTab: boolean;
  tabs: CompassTab[];
  totalUnreadBotChatsCount: number;
  unreadChatRoomsCount: number;
  unreadChatRoomUuids: string[];
}

export const TabHeaderLegacy = ({
  tabs,
  dmSelectedTabIndex,
  setDmSelectedTabIndex,
  shouldShowMessagesTab,
  shouldShowAgentsTab,
  unreadChatRoomsCount,
  unreadChatRoomUuids,
  totalUnreadBotChatsCount,
}: TabHeaderLegacyProps) => {
  const isMobileView = useSmOrMdOrLgScreenMediaQuery();
  const tabClassName = classNames({ "text-sm": !isMobileView });

  const renderNotificationsCount = (): JSX.Element => (
    <CounterBadge count={roundedCount(unreadChatRoomsCount)} />
  );

  const renderCommunityInboxCount = (): JSX.Element => (
    <CounterBadge count={roundedCount(totalUnreadBotChatsCount)} />
  );

  const safeTabIndex = Math.min(
    dmSelectedTabIndex,
    Math.max(0, tabs.length - 1),
  );

  return (
    <Tab.Group
      className="bg-primary overflow-hidden"
      selectedIndex={safeTabIndex}
      onChange={setDmSelectedTabIndex}
    >
      <div className="border-primary border-b px-6">
        <Tab.List>
          {tabs.map(tab => {
            if (tab.value === TAB_INBOX) {
              return (
                <Tab key={TAB_INBOX} className={tabClassName}>
                  {tab.label}
                </Tab>
              );
            }
            if (tab.value === TAB_UNREAD) {
              return (
                <Tab key={TAB_UNREAD} className={tabClassName}>
                  <div className="flex items-center gap-1.5">
                    {tab.label}
                    {renderNotificationsCount()}
                  </div>
                </Tab>
              );
            }
            if (tab.value === TAB_AGENTS) {
              return (
                <Tab key={TAB_AGENTS} className={tabClassName}>
                  <div className="flex items-center gap-1.5 text-sm">
                    <Icon type="16-stars-ai" size={16} />
                    {tab.label}
                    {renderCommunityInboxCount()}
                  </div>
                </Tab>
              );
            }
            return null;
          })}
        </Tab.List>
      </div>
      <Tab.Panels>
        {shouldShowMessagesTab && (
          <Tab.Panel>
            <DMsList />
          </Tab.Panel>
        )}
        {shouldShowMessagesTab && (
          <Tab.Panel>
            {unreadChatRoomsCount === 0 ? (
              <ListWrapper>
                <NoUnreadMessages />
              </ListWrapper>
            ) : (
              <DMsList unreadChatRoomUuids={unreadChatRoomUuids} />
            )}
          </Tab.Panel>
        )}
        {shouldShowAgentsTab && (
          <Tab.Panel>
            <ListWrapper>
              <BotChatList isRightActionBlock />
            </ListWrapper>
          </Tab.Panel>
        )}
      </Tab.Panels>
    </Tab.Group>
  );
};
